<template>
  <div class="atlas-dashboard-widget">
    <LocationReviewCallout v-if="!isLoadingDashboardData && !showGetStartedWidget" class="atlas-dashboard-widget__location-review" :customerId="props.customerId" source="dashboard" />

    <!-- Standard page header -->
    <div class="standard-page-header standard-page-header--no-bottom-line atlas-dashboard-widget__header">
      <h2 class="global-h2 standard-page-header__title">
        <RouterLink
          :to="{
            name: 'CalendarPosts',
            params: { customerId: props.customerId },
          }"
        >
          Atlas Social
        </RouterLink>
      </h2>

      <div v-if="!showGetStartedWidget" class="standard-page-header__subtitle-and-link">
        <div class="standard-page-header__subtitle">Keep your social channels active with authentic local content.</div>
      </div>
    </div>
    <!-- / Standard page header -->

    <Loader v-if="isLoadingDashboardData" />

    <AtlasGetStartedWidget v-else-if="showGetStartedWidget" class="atlas-dashboard-widget__get-started" :customerId="props.customerId" :isCustomerDisabled="props.isCustomerDisabled" />
    <div v-else>
      <div v-if="dashboardData?.postsPendingApproval?.length" class="atlas-dashboard-widget__posts-list">
        <SocialPostPreview
          class="atlas-dashboard-widget__post"
          v-for="socialPost in dashboardData.postsPendingApproval.slice(0, howManyPostsToShow)"
          :key="socialPost?.id"
          :onlyAtlasReadonlyFieldsVisible="true"
          :post="socialPost"
        />
      </div>

      <div v-else-if="dashboardData" class="atlas-dashboard-widget__status-section">
        <div class="atlas-dashboard-widget__status-text">
          <div v-if="dashboardData.nextPostScheduledOn" class="atlas-dashboard-widget__status-count">
            Your next post is scheduled for {{ DayjsExtended(dashboardData.nextPostScheduledOn).format("dddd [the] Do") }}
          </div>
          <div v-if="dashboardData.nextPostGenForWeek" class="atlas-dashboard-widget__status-subtitle">
            <span>{{
              `${dashboardData.postsPerWeek * dashboardData.weeksToGenPosts} new posts will be created on ${DayjsExtended(dashboardData.nextPostGenOnWeek).format("dddd [the] Do")} for the ${dashboardData.weeksToGenPosts} week period starting ${DayjsExtended(dashboardData.nextPostGenForWeek).format("MMM Do")}`
            }}</span>
            <LinkWithIcon class="atlas-dashboard-widget__config-link" tag="router-link" isDottedUnderline color="gray" :to="{ name: 'CalendarConfig', params: { customerId: props.customerId } }">
              <template #icon><IconEmbedded name="gear_2" :size="18" /></template>
            </LinkWithIcon>
          </div>
        </div>
      </div>

      <div v-if="dashboardData" class="atlas-dashboard-widget__actions-row">
        <div class="atlas-dashboard-widget__primary-actions">
          <SrpButton
            :isDisabled="props.isCustomerDisabled"
            tag="RouterLink"
            size="small"
            :fill="dashboardData?.postsPendingApproval?.length ? 'solid' : 'outlined'"
            class="atlas-dashboard-widget__generate-post-button"
            :to="{
              name: 'CalendarPosts',
              params: { customerId: props.customerId },
              hash: `#reviewAndApprove=true`,
            }"
          >
            {{ dashboardData?.postsPendingApproval?.length ? "Review & Approve" : "View Content Calendar" }}
          </SrpButton>
          <LinkWithIcon tag="router-link" isDottedUnderline :to="{ name: 'LocationHighlightsReport', params: { customerId: props.customerId } }">Local Promotion</LinkWithIcon>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import DayjsExtended from "@logic/DayjsExtended";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import Loader from "@components/Loader/Loader.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationReviewCallout from "@components/LocationReviewCallout.vue";
import AtlasGetStartedWidget from "@views/Community/ContentCalendar/AtlasGetStartedWidget.vue";

// Types
import { CalendarDashboardSummaryResult } from "@contracts/contentCalendar";

const props = withDefaults(
  defineProps<{
    customerId: string;
    isCustomerDisabled: boolean;
  }>(),
  {
    customerId: "",
    isCustomerDisabled: false,
  }
);

// How many posts to show =====================================================
const howManyPostsToShow = 3;

// Load dashboard data ========================================================
const isLoadingDashboardData = ref<boolean>(true);
const dashboardData = ref<CalendarDashboardSummaryResult | null>(null);

const showGetStartedWidget = computed(() => dashboardData.value?.showGetStartedWidget ?? !dashboardData.value?.nextPostGenOnWeek);

async function loadDashboardData() {
  isLoadingDashboardData.value = true;
  dashboardData.value = await ContentCalendarRepo.getDashboardSummary(props.customerId);
  isLoadingDashboardData.value = false;
}

onMounted(loadDashboardData);

watch(() => props.customerId, loadDashboardData);
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Atlas dashboard widget ======================================================
.atlas-dashboard-widget {
  &__actions-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
  }

  &__primary-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    flex-shrink: 0;
    gap: 18px;
  }

  &__location-review {
    width: 100%;
    min-width: 0;
    max-width: 520px;
    margin: 0 0 18px;
  }

  &__header {
    margin-bottom: 10px;
  }

  &__get-started {
    margin-bottom: -10px;
  }

  &__posts-list {
    gap: $desktop-wide-grid-gap-width;
    // margin-bottom: 20px;
    display: flex;
    flex-wrap: nowrap;
    overflow: hidden;
    align-items: flex-start;
  }

  &__post {
    margin-top: 15px;
    margin-bottom: 20px;
    width: calc((33.3% - $desktop-wide-grid-gap-width * 2 / 3) - 2px);
    flex-shrink: 0;
  }

  &__status-section {
    margin-bottom: 20px;
  }

  &__status-count {
    font-size: 17px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
  }

  &__status-subtitle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #666;
  }

  &__config-link {
    flex-shrink: 0;
  }
}
// desktop and wider (laptop max is $laptop-max-width)
@media (min-width: $tablet-max-width) {
  .atlas-dashboard-widget__get-started {
    margin-top: -20px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .atlas-dashboard-widget {
    &__posts-list {
      gap: $laptop-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $laptop-grid-gap-width * 2 / 3);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .atlas-dashboard-widget {
    &__posts-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $tablet-large-grid-gap-width * 2 / 3);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .atlas-dashboard-widget {
    &__posts-list {
      gap: $tablet-grid-gap-width;
    }

    &__post {
      width: calc(50% - $tablet-grid-gap-width * 1 / 2);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .atlas-dashboard-widget {
    &__post {
      width: 100%;
    }
  }
}
</style>
